import { z } from 'zod'
import { toSnakeCase } from '@nwwp/utils'

/**
 * Salesforce standard Lead field limits for `FirstName`/`LastName`.
 * Single source of truth consumed by both the Short Form client schema
 * and the create-lead API handler.
 */
export const LEAD_NAME_MAX_LENGTH = { firstName: 40, lastName: 80 } as const

/** Employment status options for lead financial profiling. */
export const employmentStatusSchema = z.enum([
  'FULL_TIME',
  'PART_TIME',
  'UNEMPLOYED',
  'SELF_EMPLOYED',
  'BUSINESS_OWNER',
])

/** Marital status options (Salesforce-aligned values). Empty string represents unspecified. */
export const maritalStatusSchema = z.enum([
  'Married',
  'Single',
  'Divorced',
  'Widowed',
  'Life Partner',
  'Separated',
  'Unknown',
  '',
])

/**
 * Investable assets range buckets for lead qualification.
 * - UNDER_100K: < $100,000
 * - FROM_100_TO_250K: $100,000 - $250,000
 * - FROM_250_TO_500K: $250,000 - $500,000
 * - FROM_500K_TO_1M: $500,000 - $1,000,000
 * - OVER_1M: > $1,000,000
 */
export const investableAssetsRangeSchema = z.enum([
  'UNDER_100K',
  'FROM_100_TO_250K',
  'FROM_250_TO_500K',
  'FROM_500K_TO_1M',
  'OVER_1M',
])

/**
 * Full lead schema for wealth management prospect data.
 * Stored in DynamoDB `leads` table. Published to SNS on creation via DynamoDB streams.
 */
export const leadSchema = z.object({
  /** Segment anonymous ID for pre-authentication tracking. */
  anonymous_id: z.string().nullish(),

  // Personal Information
  first_name: z.string(),
  last_name: z.string(),
  email: z.email({ error: 'Please enter a valid email address.' }),
  /** US phone number in E.164 format: +1 followed by 10 digits (e.g. +12125551234). */
  phone: z.string().regex(/^\+1\d{10}$/),
  age: z.number().min(18).optional(),
  /** Lead origin (e.g. "web", "lead-magnet", "calendly"). */
  source: z.string().nullish(),
  marital_status: maritalStatusSchema.nullish(),
  employment_status: employmentStatusSchema.nullish(),

  // Address
  city: z.string().optional(),
  /** Two-letter US state abbreviation (e.g. "CA", "NY"). */
  state: z.string().max(2).optional(),
  zip_code: z.string().optional(),

  // Financial Information
  annual_income: z.number().nullish(),
  assets: z.number().nullish(),
  investable_assets_range: z.enum(investableAssetsRangeSchema.options, { error: 'Please select an asset range.' }),

  // Tracking Information
  /**
   * Leads coming from API at this point won't have a null external_id,
   * Once we migrate to Salesforce-only, we can remove the null check
   *
   * Keeping it nullable for now for backwards compatibility
   *
   * TODO: Remove the null check once we migrate to Salesforce-only
   */
  external_id: z.string().nullish(),

  /**
   * Raw partner-supplied external ID before source namespacing (NWA-3749).
   * Set by LeadRepository on create; never trusted from caller input.
   */
  partner_supplied_id: z.string().nullish(),

  /** Arbitrary key-value pairs for tracking context. Keys are auto-transformed to snake_case. */
  metadata: z
    .array(z.object({ key: z.string().transform(toSnakeCase), value: z.union([z.string(), z.number(), z.boolean()]) }))
    .optional(),

  /** ISO timestamp when a DRAPE/CCPA request was received for this lead. */
  drape_received_at: z.string().datetime().optional(),
})

/** Subset of lead fields captured via lead magnets (no address or detailed financials). */
export const leadMagnetLeadSchema = leadSchema
  .pick({
    anonymous_id: true,
    source: true,

    phone: true,
    age: true,
    first_name: true,
    last_name: true,
    email: true,
    annual_income: true,
    investable_assets_range: true,

    metadata: true,
    external_id: true,
    partner_supplied_id: true,
  })
  .partial({ phone: true, investable_assets_range: true })

/**
 * Sources that callers may pass explicitly in the POST /api/leads request body.
 * The API handler validates body.source against this list; gatekeeper.context.source
 * is used as the fallback when body.source is absent (NWA-3623).
 */
export const overrideableLeadSources = ['nwwp_short-form', 'nwwp_wealth-check'] as const
export type OverrideableLeadSource = (typeof overrideableLeadSources)[number]

// partner_supplied_id is derive-only (set by LeadRepository from external_id), never
// trusted from caller input — omitted from the create schemas so it can't be passed (NWA-3749).
export const createLeadSchema = leadSchema.omit({ partner_supplied_id: true })

export const createLeadMagnetLeadSchema = leadMagnetLeadSchema
  .omit({ partner_supplied_id: true })
  .required({ investable_assets_range: true })

const leadMagnetLeadCreateSchema = leadMagnetLeadSchema.omit({ partner_supplied_id: true })
export type LeadMagnetLeadCreate = z.infer<typeof leadMagnetLeadCreateSchema>

export type Lead = z.infer<typeof leadSchema>
export type LeadMagnetLead = z.infer<typeof leadMagnetLeadSchema>
export type CreateLead = z.infer<typeof createLeadSchema>
export type CreateLeadMagnetLead = z.infer<typeof createLeadMagnetLeadSchema>
export type EmploymentStatus = z.infer<typeof employmentStatusSchema>
export type InvestableAssetsRange = z.infer<typeof investableAssetsRangeSchema>
export type MaritalStatus = z.infer<typeof maritalStatusSchema>
